import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import Cookies from 'js-cookie';
import { OriginatorContextType, OriginatorType } from './types';
import { ORIGINATOR_COOKIE } from './constants';
import { useAppContext } from 'app/context/AppContextProvider';
import { OriginatorServiceHttpService } from './OriginatorService';
import { useAuthContext } from 'modules/auth/context';
import { useSnackbar } from 'app/hooks/useSnackbar';

export const OriginatorContext = createContext<OriginatorContextType>({
  originators: [],
  selectedOriginator: null,
  isLoadingOriginator: false,
  getOriginatorFundings: () => {
    throw new Error('Método não implementado');
  },
  setSelectedOriginator: () => {
    throw new Error('Método não implementado');
  },
  getOriginator: () => {
    throw new Error('Método não implementado');
  },
});

export interface IOriginatorProviderProps {
  children?: React.ReactNode;
}

export const OriginatorProvider = ({ children }: IOriginatorProviderProps) => {
  const { is_issuer_app } = useAppContext();
  const { isLogged } = useAuthContext();
  const { showSnackbar } = useSnackbar();
  const [isLoadingOriginator, setIsLoadingOriginator] =
    useState<boolean>(false);
  const [originators, setOriginators] = useState<OriginatorType[]>([]);
  const [selectedOriginator, setSelectedOriginator] = useState<null | string>(
    null,
  );

  const onSelectOriginator = useCallback(
    (originator: string | null) => {
      if (is_issuer_app) {
        if (originator === null) {
          Cookies.remove(ORIGINATOR_COOKIE);
        } else {
          Cookies.set(ORIGINATOR_COOKIE, originator);
        }
        setSelectedOriginator(originator);
      }
    },
    [is_issuer_app],
  );

  const getOriginatorFundings = useCallback(async (originatorId: string) => {
    try {
      setIsLoadingOriginator(true);
      const originatorService = new OriginatorServiceHttpService();
      const fundings = await originatorService.getOriginatorFundings(
        originatorId,
      );
      return fundings.content;
    } catch (error) {
      showSnackbar('Ocorreu um erro ao buscar os fundings', 'error');
      return [];
    } finally {
      setIsLoadingOriginator(false);
    }
  }, []);

  const getOriginator = async (originatorId: string) => {
    try {
      setIsLoadingOriginator(true);
      const originatorService = new OriginatorServiceHttpService();
      const res = await originatorService.getOriginator(originatorId);

      if (res) {
        return res;
      }

      return null;
    } catch (err) {
      console.error('Erro getOriginator', err);
      return null;
    } finally {
      setIsLoadingOriginator(false);
    }
  };

  useEffect(() => {
    if (is_issuer_app && isLogged) {
      const originatorService = new OriginatorServiceHttpService();
      originatorService.getOriginators().then((res) => {
        setOriginators(res.content);
      });
    }
  }, [is_issuer_app, isLogged]);

  return (
    <OriginatorContext.Provider
      value={{
        originators,
        selectedOriginator,
        isLoadingOriginator,
        getOriginatorFundings,
        setSelectedOriginator: onSelectOriginator,
        getOriginator,
      }}
    >
      {children}
    </OriginatorContext.Provider>
  );
};

export const useOriginatorContext = () => {
  const context = useContext(OriginatorContext);

  if (context === undefined) {
    throw new Error(
      'useOriginatorContext must be used within a OriginatorProvider',
    );
  }
  return context;
};
